/**
 * Built-in student avatars. Bundled SVGs only — no uploads, no photo storage.
 */
import { cn } from "@/lib/utils";

export type AvatarId = "quantum" | "nova" | "orbit" | "prism";

export const AVATARS: { id: AvatarId; name: string; from: string; to: string }[] = [
  { id: "quantum", name: "Quantum", from: "#6FAE35", to: "#12321D" },
  { id: "nova", name: "Nova", from: "#38BDF8", to: "#1E3A8A" },
  { id: "orbit", name: "Orbit", from: "#FBBF24", to: "#7C2D12" },
  { id: "prism", name: "Prism", from: "#C084FC", to: "#312E81" },
];

export const DEFAULT_AVATAR: AvatarId = "quantum";

export function isAvatarId(value: unknown): value is AvatarId {
  return AVATARS.some((a) => a.id === value);
}

export function StudentAvatar({
  id,
  className,
}: {
  id?: string | null;
  className?: string;
}) {
  const avatar = AVATARS.find((a) => a.id === id) ?? AVATARS[0];
  const gid = `qm-av-${avatar.id}`;

  return (
    <svg
      viewBox="0 0 64 64"
      role="img"
      aria-label={`${avatar.name} avatar`}
      className={cn("size-9 rounded-full", className)}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={avatar.from} />
          <stop offset="100%" stopColor={avatar.to} />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="32" fill={`url(#${gid})`} />
      {/* shoulders */}
      <path
        d="M12 60c2-11 10-17 20-17s18 6 20 17z"
        fill="rgba(255,255,255,0.9)"
        opacity="0.92"
      />
      {/* head */}
      <circle cx="32" cy="27" r="11" fill="rgba(255,255,255,0.95)" />
      {/* orbit ring — the Quantum Minds motif */}
      <ellipse
        cx="32"
        cy="27"
        rx="19"
        ry="7.5"
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="1.6"
        transform="rotate(-22 32 27)"
      />
      <circle cx="49" cy="20" r="2.6" fill="rgba(255,255,255,0.95)" />
    </svg>
  );
}
